import { useId, useMemo, useState } from 'react';

import { formatNumber, matchesQuery } from '@shared/format';

import { cn } from '../cn';

/**
 * One component for every «pick from a set that already exists» — the shelf
 * source parameter, a shelf's hand-picked title set, a credit's title, a tag
 * field.
 *
 * **It searches only what is in its list and never proposes a new value.** A
 * search that offered to create one would be free-text entry through the back
 * door, and free-text entry is exactly what the catalog's «chosen from an
 * existing vocabulary, never typed free-form» rule exists to prevent: an
 * operator could author a shelf pointing at a category the platform does not
 * hold, and the shelf would render empty with nothing on screen explaining why.
 *
 * **One picker, two modes, declared not inferred.** `single` replaces the
 * chosen value; `multi` appends, because an ordered set's new member joins at
 * its end and the order is the payload rather than a view preference.
 *
 * **The chosen values sit above the search box** so they hold still while the
 * list filters beneath them, and each carries its own remove control.
 *
 * **An already-chosen option stays in the list and is announced rather than
 * removed from it.** Dropping it would change the announced result count for a
 * reason the reader cannot see — and the count *is* announced, through one
 * polite line that serves the sighted reader and the screen reader alike.
 *
 * Arabic folding runs on the **comparison** only. Nothing displayed is
 * rewritten.
 */

export interface PickerOption {
  id: string;
  label: string;
  /**
   * A qualifier the reader needs before choosing — a title's publish state, say.
   *
   * It is **not** searched: the search folds and matches the label, which is the
   * name the operator knows the record by. A note that matched would make
   * «مسودة» a query that returns every draft, which is a filter this surface
   * does not offer and cannot explain.
   */
  note?: string;
}

export interface VocabularyPickerCopy {
  searchLabel: string;
  searchPlaceholder: string;
  /** `n` arrives already formatted for the human surface. */
  resultCount: (count: string) => string;
  noResults: string;
  remove: (label: string) => string;
}

export function VocabularyPicker({
  mode,
  options,
  value,
  onChange,
  copy,
  labelForUnknown,
  onSearch,
  disabled = false,
}: {
  mode: 'single' | 'multi';
  options: readonly PickerOption[];
  /** Ordered ids. Order is meaning in `multi` mode. */
  value: readonly string[];
  onChange: (next: string[]) => void;
  copy: VocabularyPickerCopy;
  /**
   * A chosen id the loaded vocabulary page does not carry. Rendering the raw
   * identifier is a visible degradation; silently dropping the chip would
   * destroy an operator's selection on the next save.
   */
  labelForUnknown?: (id: string) => string;
  /**
   * Hand the typed term to the caller, for a vocabulary the client does not hold.
   *
   * **Absent means the whole vocabulary is in `options`** and the filter below is
   * the whole search — which is true of the tag list, and was wrongly assumed of
   * the catalogue. A picker over 357 titles that had been handed the first 20
   * answered «لا عنوان بهذا الاسم» for a title that plainly exists, which is a
   * wrong answer stated as a fact rather than a ceiling the operator could see.
   *
   * When it is supplied the caller re-reads on each term and `options` is the
   * answer to what was typed, so the filter below becomes a no-op over an
   * already-matching set rather than a second, narrower search.
   */
  onSearch?: ((term: string) => void) | undefined;
  disabled?: boolean;
}) {
  const [term, setTerm] = useState('');
  const searchId = useId();
  const countId = useId();

  const labels = useMemo(() => new Map(options.map((option) => [option.id, option.label])), [options]);
  const notes = useMemo(
    () => new Map(options.flatMap((option) => (option.note ? [[option.id, option.note] as const] : []))),
    [options],
  );

  const visible = useMemo(
    () => options.filter((option) => matchesQuery(option.label, term)),
    [options, term],
  );

  function labelOf(id: string): string {
    return labels.get(id) ?? labelForUnknown?.(id) ?? id;
  }

  function noteOf(id: string): string | undefined {
    return notes.get(id);
  }

  function toggle(id: string) {
    if (mode === 'single') {
      onChange([id]);
      return;
    }
    if (value.includes(id)) return;
    onChange([...value, id]);
  }

  return (
    <div className="grid gap-2">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {value.map((id) => {
            const label = labelOf(id);
            const known = labels.has(id);

            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-pill border border-border bg-surface-raised py-1 pe-1 ps-3 text-sm"
              >
                <bdi className={known ? undefined : 'mono'}>{label}</bdi>
                {noteOf(id) ? <span className="text-xs text-ink-muted">{noteOf(id)}</span> : null}
                <button
                  type="button"
                  disabled={disabled}
                  aria-label={copy.remove(label)}
                  onClick={() => onChange(value.filter((current) => current !== id))}
                  className="flex size-6 items-center justify-center rounded-sm text-ink-muted hover:bg-hover-bg hover:text-ink disabled:opacity-45"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      ) : null}

      <label className="sr-only" htmlFor={searchId}>
        {copy.searchLabel}
      </label>
      <input
        id={searchId}
        type="search"
        value={term}
        disabled={disabled}
        aria-describedby={countId}
        placeholder={copy.searchPlaceholder}
        onChange={(event) => {
          setTerm(event.target.value);
          onSearch?.(event.target.value);
        }}
        className="hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink placeholder:text-ink-faint disabled:opacity-45"
      />

      {/*
        One polite line carries the filter's effect to both readers. It is empty
        — and therefore silent — while no term is typed, because an unfiltered
        list has no result count to report.
      */}
      <p id={countId} aria-live="polite" className="m-0 min-h-4 text-xs text-ink-muted">
        {term ? (visible.length ? copy.resultCount(formatNumber(visible.length)) : copy.noResults) : ''}
      </p>

      {/*
        Capped and scrolling inside itself: a genre tree grows, and a dialog
        that grows with it pushes its own save button off the screen. Roughly
        four options are visible, which is enough for the list to read as
        scrollable.
      */}
      <ul className="m-0 max-h-[9rem] list-none overflow-y-auto rounded-sm border border-border bg-surface p-1 [overscroll-behavior-block:contain]">
        {visible.map((option) => {
          const chosen = value.includes(option.id);

          return (
            <li key={option.id}>
              <button
                type="button"
                disabled={disabled}
                aria-pressed={chosen}
                onClick={() => toggle(option.id)}
                className={cn(
                  'hit-target flex w-full items-center rounded-sm px-3 py-2 text-start text-sm disabled:opacity-45',
                  chosen
                    ? 'bg-accent-soft text-accent-text'
                    : 'text-ink-muted hover:bg-hover-bg hover:text-ink',
                )}
              >
                <span className="flex w-full items-baseline justify-between gap-2">
                  <bdi>{option.label}</bdi>
                  {option.note ? (
                    <span className="shrink-0 text-xs text-ink-faint">{option.note}</span>
                  ) : null}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
